import {
  Button,
  Col,
  Descriptions,
  Modal,
  Row,
  Show,
  Space,
  Table,
  Tag,
  TextField,
  Tooltip,
} from "@pankod/refine-antd";
import { IResourceComponentsProps, useShow } from "@pankod/refine-core";
import { useMemo, useState } from "react";
import "react-mde/lib/styles/css/react-mde-all.css";
import { ITransaction } from "types/transaction.interface";
import type { ColumnsType } from "antd/es/table";
import { ITrack } from "types/track.interface";
import { IRelease } from "types/releaase.interface";
import { IUser } from "types/user.interface";
import { formatDateTime } from "utils";
import { Link } from "react-router-dom";
import { Icons } from "@pankod/refine-antd";
import { message } from "antd";
import axiosService from "services/axios.service";

export const OrderShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<ITransaction>();
  const { data, isLoading } = queryResult;
  const record: any = data?.data;

  // Modal state for viewing user orders
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState<any>(null);
  const [modalOrders, setModalOrders] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPagination, setModalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Load user orders for modal
  const loadUserOrders = async (
    userId: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    setModalLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        perPage: pageSize.toString(),
        userId: userId,
      });

      const response = await axiosService.get(
        `/v1/admin/orders?${queryParams.toString()}`
      );

      const data = response?.data?.data;
      setModalOrders(data?.data || []);
      setModalPagination({
        current: data?.currentPage || page,
        pageSize: data?.perPage || pageSize,
        total: data?.totalData || 0,
      });
    } catch (error) {
      console.error("Error loading user orders:", error);
      message.error("Failed to load user orders");
    } finally {
      setModalLoading(false);
    }
  };

  // Open modal to view user orders
  const handleViewUserOrders = async (user: any) => {
    setModalUser(user);
    setIsModalOpen(true);
    await loadUserOrders(user._id);
  };

  // Handle modal pagination change
  const handleModalPageChange = (page: number, pageSize?: number) => {
    if (modalUser) {
      loadUserOrders(modalUser._id, page, pageSize || modalPagination.pageSize);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalUser(null);
    setModalOrders([]);
    setModalPagination({ current: 1, pageSize: 10, total: 0 });
  };

  // Release Tracks Modal state
  const [isTracksModalOpen, setIsTracksModalOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<IRelease | null>(null);

  // Open tracks modal
  const handleViewReleaseTracks = (release: IRelease) => {
    setSelectedRelease(release);
    setIsTracksModalOpen(true);
  };

  // Close tracks modal
  const handleCloseTracksModal = () => {
    setIsTracksModalOpen(false);
    setSelectedRelease(null);
  };

  const columnsTrack: ColumnsType<ITrack> = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: (value: any) => <TextField value={value} />,
      },
      {
        title: "Artist",
        dataIndex: "artist",
        key: "artist",
        render: (value) => <TextField value={value} />,
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (value) => <TextField value={`$${value}`} />,
      },
    ],
    []
  );

  const columnsReleases: ColumnsType<IRelease> = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: (value: string) => <TextField value={value} />,
      },
      {
        title: "Contributor",
        dataIndex: "user",
        key: "user",
        render: (user: IUser) => (
          <TextField value={user?.username || user?.email || "N/A"} />
        ),
      },
      {
        title: "Tracks",
        dataIndex: "tracks",
        key: "tracks",
        render: (tracks: ITrack[], release: IRelease) => (
          <Button
            type="link"
            style={{ padding: 0, color: "#1890ff" }}
            onClick={() => handleViewReleaseTracks(release)}
          >
            {tracks?.length || 0} tracks
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <Show isLoading={isLoading} canEdit={false}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Order Summary */}
        <Descriptions
          title="Order Information"
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label="Order ID">
            <strong>{record?.data.orderId}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag
              color={
                record?.data.status === "COMPLETED"
                  ? "green"
                  : record?.data.status === "PENDING"
                  ? "orange"
                  : "red"
              }
            >
              {record?.data.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            {formatDateTime(record?.data.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="PayPal Transaction ID">
            {record?.data.paypalTransactionId}
          </Descriptions.Item>
          <Descriptions.Item label="PayPal Email">
            {record?.data.paypalEmail}
          </Descriptions.Item>
          <Descriptions.Item label="IP Address">
            {record?.data.ipAddress || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Country">
            {record?.data.country || "N/A"}
          </Descriptions.Item>
        </Descriptions>

        {/* User Information */}
        <Descriptions
          title="Buyer Information"
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          extra={
            <Tooltip title="View all orders from this user">
              <Button
                type="primary"
                icon={<Icons.UserOutlined />}
                onClick={() => handleViewUserOrders(record?.data.user)}
              >
                View All User Orders
              </Button>
            </Tooltip>
          }
        >
          <Descriptions.Item label="User ID">
            {record?.data.user?._id}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {record?.data.user?.email}
          </Descriptions.Item>
          <Descriptions.Item label="Username">
            {record?.data.user?.username || "N/A"}
          </Descriptions.Item>
        </Descriptions>

        {/* Tracks */}
        {record?.data.tracks && record?.data.tracks.length > 0 && (
          <div>
            <h3 style={{ marginBottom: "16px" }}>
              <Icons.FileTextOutlined /> Tracks Purchased (
              {record?.data.tracks.length})
            </h3>
            <Table
              dataSource={record?.data.tracks}
              columns={columnsTrack}
              pagination={false}
              rowKey="_id"
            />
            <Descriptions bordered column={3} style={{ marginTop: "16px" }}>
              <Descriptions.Item label="Track Discount">
                <Tag color="red">
                  {record?.data.trackDiscount.toFixed(2)}% OFF
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Track Cost">
                <strong style={{ fontSize: "16px" }}>
                  ${record?.data.trackCost.toFixed(2)}
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* Releases */}
        {record?.data.releases && record?.data.releases.length > 0 && (
          <div>
            <h3 style={{ marginBottom: "16px" }}>
              <Icons.FolderOutlined /> Releases Purchased (
              {record?.data.releases.length})
            </h3>
            <Table
              dataSource={record?.data.releases}
              columns={columnsReleases}
              pagination={false}
              rowKey="_id"
            />
            <Descriptions bordered column={3} style={{ marginTop: "16px" }}>
              <Descriptions.Item label="Release Discount">
                <Tag color="red">
                  {record?.data.releaseDiscount.toFixed(2)}% OFF
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Release Cost">
                <strong style={{ fontSize: "16px" }}>
                  ${record?.data.releaseCost.toFixed(2)}
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* Total Cost */}
        <Descriptions bordered>
          <Descriptions.Item label="Total Cost">
            <strong style={{ fontSize: "20px", color: "#52c41a" }}>
              ${record?.data.totalCost.toFixed(2)}
            </strong>
          </Descriptions.Item>
        </Descriptions>
      </Space>

      {/* User Orders Modal */}
      <Modal
        title={
          modalUser ? (
            <div style={{ fontSize: 16 }}>
              <Icons.UserOutlined /> User Information: {modalUser.email}
              {modalUser.username && ` (@${modalUser.username})`}
            </div>
          ) : (
            "User Information"
          )
        }
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={"95vw !important"}
        centered
        style={{ top: 20, maxWidth: "95vw" }}
        bodyStyle={{ maxHeight: "85vh", overflowY: "auto", padding: 24 }}
      >
        {/* User Information Section */}
        {modalUser && (
          <div
            style={{
              marginBottom: 24,
              padding: 20,
              background: "#f5f5f5",
              borderRadius: 8,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {modalUser.avatar && (
                    <img
                      src={modalUser.avatar}
                      alt="avatar"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, marginBottom: 8 }}>
                      {modalUser.firstName || modalUser.lastName
                        ? `${modalUser.firstName || ""} ${
                            modalUser.lastName || ""
                          }`.trim()
                        : modalUser.email}
                    </h2>
                    <div style={{ color: "#666", marginBottom: 4 }}>
                      <Icons.MailOutlined /> {modalUser.email}
                    </div>
                    {modalUser.username && (
                      <div style={{ color: "#666", marginBottom: 4 }}>
                        <Icons.UserOutlined /> @{modalUser.username}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>Role</div>
                  <div style={{ fontWeight: 600 }}>
                    {modalUser.role === "admin"
                      ? "Admin"
                      : modalUser.isContributor
                      ? "Contributor"
                      : "User"}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>
                    Stars Remaining
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {modalUser.starsRemaining || 0}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>Status</div>
                  <div>
                    <Tag color={modalUser.isEnabled ? "green" : "red"}>
                      {modalUser.isEnabled ? "Active" : "Blocked"}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ color: "#999", fontSize: 12 }}>
                    Member Since
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {formatDateTime(modalUser.createdAt)}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Orders Section */}
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>
            <Icons.ShoppingOutlined /> Order History ({modalPagination.total})
          </h3>
        </div>
        <Table
          dataSource={modalOrders}
          loading={modalLoading}
          rowKey="_id"
          pagination={{
            current: modalPagination.current,
            pageSize: modalPagination.pageSize,
            total: modalPagination.total,
            onChange: handleModalPageChange,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} orders`,
          }}
          scroll={{ x: 1000 }}
        >
          <Table.Column
            dataIndex="createdAt"
            key="createdAt"
            title="Date"
            width={150}
            render={(value) => <TextField value={formatDateTime(value)} />}
          />
          <Table.Column
            dataIndex="orderId"
            key="orderId"
            title="Order ID"
            width={120}
            render={(value) => <TextField value={value} />}
          />
          <Table.Column
            dataIndex="tracks"
            key="tracks"
            title="Tracks"
            width={80}
            render={(value) => <TextField value={value?.length || 0} />}
          />
          <Table.Column
            dataIndex="releases"
            key="releases"
            title="Releases"
            width={80}
            render={(value) => <TextField value={value?.length || 0} />}
          />
          <Table.Column
            dataIndex="status"
            key="status"
            title="Status"
            width={100}
            render={(value) => (
              <Tag
                color={
                  value === "COMPLETED"
                    ? "green"
                    : value === "PENDING"
                    ? "orange"
                    : "red"
                }
              >
                {value}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="totalCost"
            key="totalCost"
            title="Total"
            width={100}
            render={(value) => (
              <TextField
                value={`$${value?.toFixed(2)}`}
                style={{ fontWeight: 600 }}
              />
            )}
          />
          <Table.Column
            dataIndex=""
            key="actions"
            title="Actions"
            width={100}
            fixed="right"
            render={(value, item: any) => (
              <Space>
                <Tooltip title="View order details">
                  <Link to={`/orders/show/${item._id}`} target="_blank">
                    <Button size="small" icon={<Icons.EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Space>
            )}
          />
        </Table>
      </Modal>

      {/* Release Tracks Modal */}
      <Modal
        title={
          selectedRelease ? (
            <div style={{ fontSize: 16 }}>
              <Icons.FolderOutlined /> Tracks in: {selectedRelease.title}
            </div>
          ) : (
            "Release Tracks"
          )
        }
        visible={isTracksModalOpen}
        onCancel={handleCloseTracksModal}
        footer={null}
        width={800}
        centered
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 24 }}
      >
        {selectedRelease && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Tag color="blue">
                {selectedRelease.tracks?.length || 0} tracks
              </Tag>
            </div>
            <Table
              dataSource={selectedRelease.tracks || []}
              rowKey="_id"
              pagination={false}
              size="small"
            >
              <Table.Column
                dataIndex="title"
                key="title"
                title="Title"
                render={(value) => <TextField value={value} />}
              />
              <Table.Column
                dataIndex="artist"
                key="artist"
                title="Artist"
                render={(value) => <TextField value={value || "N/A"} />}
              />
              <Table.Column
                dataIndex="price"
                key="price"
                title="Price"
                width={100}
                render={(value) => (
                  <TextField value={value ? `$${value}` : "N/A"} />
                )}
              />
            </Table>
          </>
        )}
      </Modal>
    </Show>
  );
};
